import { NavigateFunction } from 'react-router-dom';
import {
	clearEventBookingWizardData,
	clearOldEventBookingWizardData,
} from 'Views/Components/_HumanWritten/BookingWizard/EventsBookingWizard/EventsBookingWizardData';
import { clearBookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardDataStore';
import { clearStorageTransactionPrice } from '../CartPriceSessionStorage';
import {
	clearEventBookingTransactionIdFromStorage,
	clearFerryBookingTransactionIdFromStorage,
} from 'Services/Api/_HumanWritten/BookingService/BookingService';

/**
 * The method to be set as the 'onSuccess' method of the booking wizard.
 * Clears the booking wizard data in the local storage and the cart prices in the store, then redirects to the
 * ferry schedule page.
 * @param navigate
 * @param transactionId
 * @param alteration
 * @param returnTrip
 * @param eventBooking
 * @param userId
 * @param stopNavigation
 */
export function onWizardSuccess(
	navigate: NavigateFunction,
	transactionId: string,
	alteration: boolean,
	returnTrip: boolean,
	eventBooking: boolean,
	userId?: string,
	stopNavigation?: boolean,
	/**
	 * When pay later splits a cart into several transactions, the full list of resulting
	 * transaction ids. The success page is sent all of them via `?ids=` so it can render every
	 * booking. Falls back to the single `transactionId` (`?id=`) when omitted.
	 */
	transactionIds?: string[],
) {
	/**
	 * Clear the local storage of booking wizard data
	 * This should be fine as all payments should have been successful and all
	 * relevant Bookings should now be in the database.
	 */
	// store.clearBookingPrices();

	if (stopNavigation) {
		// Still need to clear data even if not navigating
		if (eventBooking) {
			clearEventBookingWizardData();
			clearOldEventBookingWizardData();
			clearEventBookingTransactionIdFromStorage();
		} else {
			clearBookingWizardData();
			clearFerryBookingTransactionIdFromStorage();
		}
		return;
	}

	// Clear storage-only data BEFORE navigating. These are read by the wizard's step-validation
	// guards — if they're still present when React re-renders the wizard during the navigation
	// transition, the guards can redirect to an earlier tab (e.g. tickets) before the success
	// page mounts. Clearing them first ensures the guards can't interfere.
	if (eventBooking) {
		clearEventBookingTransactionIdFromStorage();
	} else {
		clearFerryBookingTransactionIdFromStorage();
	}

	const baseURL = eventBooking ? '/event-booking-success' : '/booking-success';
	const destination =
		transactionIds && transactionIds.length > 1
			? `${baseURL}?ids=${transactionIds.join(',')}&ui=${userId}`
			: `${baseURL}?id=${transactionId}&ui=${userId}`;
	navigate(destination);

	// Clear wizard store data after navigation is initiated. Use a longer delay to ensure
	// the route change has taken effect and the wizard component has unmounted, so clearing
	// the store doesn't trigger a re-render of the wizard's step-validation guards.
	setTimeout(() => {
		if (eventBooking) {
			clearEventBookingWizardData();
			clearOldEventBookingWizardData();
		} else {
			clearBookingWizardData();
		}
	}, 100);
}

/**
 * Removes the booking wizard data and booking ids stored in local storage.
 */
export function clearAllFerryBookingWizardDataFromStorage() {
	// This will clear the new and old booking wizard data (if any)
	clearBookingWizardData();
	clearFerryBookingTransactionIdFromStorage();
}

/**
 * Removes the event booking wizard data and booking ids stored in local storage.
 */
export function clearAllEventBookingWizardDataFromStorage() {
	clearEventBookingWizardData();
	clearEventBookingTransactionIdFromStorage();
	clearOldEventBookingWizardData();
}

export function clearFerryBookingWizard() {
	// This will clear the new and old booking wizard data (if any)
	clearBookingWizardData();
	clearFerryBookingTransactionIdFromStorage();
	clearStorageTransactionPrice();
}

export function clearEventBookingWizard() {
	clearEventBookingWizardData();
	clearEventBookingTransactionIdFromStorage();
	clearOldEventBookingWizardData();
	clearStorageTransactionPrice();
}
